import { useState } from "react";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { ICategoryFilterResponse, IMaterialsResponse } from "@miplatform/types";
import {
  getSelectedAttributeValues,
  sortCategoryAttributes,
} from "@miplatform/utils";
import { useCategories, useMaterials } from "@mibuildweb/hooks";

export const useItemsViewModel = () => {
  const [filteredMaterials, setFilteredMaterials] = useState<
    IMaterialsResponse[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<
    Partial<ICategoryFilterResponse>
  >({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<
    Record<string, string[]>
  >({});
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search);

  const {
    materials,
    refresh,
    getMaterials,
    setMaterials,
    deleteMaterial,
    isLoading: isMaterialsLoading,
  } = useMaterials();

  const {
    getCategoryFilters,
    categoryFilterOptions,
    setCategoryFilterOptions,
    isLoading: isCategoriesLoading,
  } = useCategories();

  const fetchMaterials = () => getMaterials();

  const fetchCategoryFilters = async () => {
    const response = await getCategoryFilters();
    if (response.status === HTTP_STATUSES.OK) {
      setCategoryFilterOptions(response.data);
    }
  };

  const handleReset = () => {
    setSelectedCategory({});
    setSelectedAttributeValues({});
    setSearch("");
  };

  const handleCategoryClick = (category: ICategoryFilterResponse) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
  };

  const handleAttributeValueClick = (
    attributeId: string,
    attributeValue: string
  ) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
  };

  const hasFilters =
    selectedCategory.categoryId ||
    Object.keys(selectedAttributeValues).length > 0 ||
    search;

  return {
    materials,
    filteredMaterials,
    setMaterials,
    fetchMaterials,
    fetchCategoryFilters,
    refresh,
    deleteMaterial,
    categoryFilterOptions,
    search,
    debouncedSearch,
    selectedCategory,
    selectedAttributeValues,
    setSearch,
    handleReset,
    handleCategoryClick,
    handleAttributeValueClick,
    isLoading: isMaterialsLoading || isCategoriesLoading,
    setFilteredMaterials,
    hasFilters,
  };
};
